import cn from "classnames";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Frame } from "shared/datamodel/schemas";
import styles from "./frame-actions.module.css";
import { Menu, MenuItem } from "frontend/ui-components/dropdown-menu/dropdown-menu";
import { MenuLabel } from "frontend/ui-components/dropdown-menu/label/label";
import { CimRenameIcon } from "frontend/icons/rename";
import { OpenedEyeIcon } from "frontend/icons/opened-eye-icon";
import { CimClosedEyeIcon } from "frontend/icons/closed-eye-icon";
import { CimTrashIcon } from "frontend/icons/trash-icon";
import { useHover } from "frontend/ui-components/floaters/use-hover";

type FrameActionsProps = {
  id: string;
  frame: Frame;
  isSelected: boolean;
  onFrameThumbnailClick: (frame: Frame) => void;
  onFrameVisibilityChange: (id: string, visible: boolean) => void;
  onFrameDelete: (id: string) => void;
  onFrameTitleChange: (id: string, title: string) => void;
};

export const FrameActions = ({
  id,
  frame,
  isSelected,
  onFrameThumbnailClick,
  onFrameVisibilityChange,
  onFrameTitleChange,
  onFrameDelete,
  children,
}: PropsWithChildren<FrameActionsProps>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(frame.title);
  const inputRef = useRef<HTMLInputElement>(null);
  const { visible = true } = frame;
  const frameThumbnailRef = useRef<HTMLDivElement>(null);
  const frameThumbnailHover = useHover(frameThumbnailRef);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setIsMenuOpen(frameThumbnailHover);
  }, [frameThumbnailHover]);

  return (
    <div
      key={id}
      className={styles.frameThumbnailContainer}
      data-selected={isSelected}
      onClick={() => onFrameThumbnailClick(frame)}
      ref={frameThumbnailRef}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          defaultValue={title}
          placeholder="Frame"
          className={styles.frameTitleInput}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          onBlur={() => {
            onFrameTitleChange(`frame-${id}`, title.trim() || "Frame");
            setIsEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onFrameTitleChange(`frame-${id}`, title.trim() || "Frame");
              setIsEditing(false);
            }
          }}
        />
      ) : (
        <span className={styles.frameThumbnailLabel} onDoubleClick={() => setIsEditing(true)}>
          {frame.title}
        </span>
      )}
      {children}

      <Menu
        className={styles.frameThumbnailMenu}
        label={
          <div className={styles.frameThumbnailDots}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="3" fill="none" viewBox="0 0 14 3">
              <circle cx="1.327" cy="1.327" r="1.327" fill="#AFB3BF"></circle>
              <circle cx="6.634" cy="1.327" r="1.327" fill="#AFB3BF"></circle>
              <circle cx="11.943" cy="1.327" r="1.327" fill="#AFB3BF"></circle>
            </svg>
          </div>
        }
        showLabel={isMenuOpen || frameThumbnailHover}
        onOpenChange={(isOpen) => {
          setIsMenuOpen(isOpen);
        }}
        offset={-1}
      >
        <div className={styles.frameThumbnailMenuItems}>
          <MenuItem
            label={<MenuLabel icon={<CimRenameIcon />} label="Rename" />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsEditing(true);
              setTimeout(() => {
                inputRef.current?.focus();
              }, 100);
            }}
            className={cn(styles.frameMenuItem, styles.frameRenameIcon)}
          />
          {visible ? (
            <MenuItem
              label={<MenuLabel icon={<CimClosedEyeIcon width={12} height={5} />} label="Hide" />}
              onClick={() => {
                onFrameVisibilityChange(`frame-${id}`, false);
              }}
              className={styles.frameMenuItem}
            />
          ) : (
            <MenuItem
              label={<MenuLabel icon={<OpenedEyeIcon color={"#1e1e1e"} size={17} />} label="Show" />}
              onClick={() => {
                onFrameVisibilityChange(`frame-${id}`, true);
              }}
              className={styles.frameMenuItem}
            />
          )}
          <MenuItem
            label={<MenuLabel icon={<CimTrashIcon width={10} height={11} />} label="Delete" />}
            onClick={() => {
              onFrameDelete(`frame-${id}`);
            }}
            className={styles.frameMenuItem}
          />
        </div>
      </Menu>
    </div>
  );
};
